import { useState, useEffect } from 'react';
import { Users, Shield, PenTool, User, Ban, Trash2, Check, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';

const roleConfig = {
  admin: { label: 'Admin', icon: Shield, color: 'bg-purple-100 text-purple-800' },
  author: { label: 'Author', icon: PenTool, color: 'bg-blue-100 text-blue-800' },
  user: { label: 'User', icon: User, color: 'bg-gray-100 text-gray-800' },
};

const statusConfig = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-800' },
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [suspendDialog, setSuspendDialog] = useState({ open: false, user: null, action: null });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole, userName) => {
    setUpdating(userId);
    try {
      await adminAPI.updateUserRole(userId, newRole);
      toast.success(`${userName}'s role updated to ${roleConfig[newRole].label}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setUpdating(null);
    }
  };

  const handleSuspend = async () => {
    const { user, action } = suspendDialog;
    if (!user) return;
    
    setUpdating(user.id);
    try {
      if (action === 'suspend') {
        await adminAPI.suspendUser(user.id);
        toast.success(`${user.name} has been suspended`);
      } else {
        await adminAPI.unsuspendUser(user.id);
        toast.success(`${user.name} has been unsuspended`);
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    } finally {
      setUpdating(null);
      setSuspendDialog({ open: false, user: null, action: null });
    }
  };

  const handleDelete = async () => {
    const { user } = deleteDialog;
    if (!user) return;
    
    setUpdating(user.id);
    try {
      const response = await adminAPI.deleteUser(user.id);
      toast.success(
        `${user.name} deleted. ${response.data.deleted_comments} comments removed, ${response.data.preserved_articles} articles preserved.`
      );
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setUpdating(null);
      setDeleteDialog({ open: false, user: null });
    }
  };

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    authors: users.filter(u => u.role === 'author').length,
    users: users.filter(u => u.role === 'user').length,
    suspended: users.filter(u => u.status === 'suspended').length,
  };

  return (
    <div data-testid="admin-users">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
          User Management
        </h1>
        <p className="text-muted-foreground">
          Manage user roles, suspend, or delete users.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
        <div className="bg-background p-4 border border-border">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8 text-accent" />
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-background p-4 border border-border">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.admins}</p>
              <p className="text-sm text-muted-foreground">Admins</p>
            </div>
          </div>
        </div>
        <div className="bg-background p-4 border border-border">
          <div className="flex items-center gap-3">
            <PenTool className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.authors}</p>
              <p className="text-sm text-muted-foreground">Authors</p>
            </div>
          </div>
        </div>
        <div className="bg-background p-4 border border-border">
          <div className="flex items-center gap-3">
            <User className="h-8 w-8 text-gray-600" />
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.users}</p>
              <p className="text-sm text-muted-foreground">Regular Users</p>
            </div>
          </div>
        </div>
        <div className="bg-background p-4 border border-border">
          <div className="flex items-center gap-3">
            <Ban className="h-8 w-8 text-red-600" />
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.suspended}</p>
              <p className="text-sm text-muted-foreground">Suspended</p>
            </div>
          </div>
        </div>
      </div>

      {/* Role Legend */}
      <div className="bg-muted/30 p-4 rounded-md mb-6">
        <h3 className="font-medium text-foreground mb-2">Role Permissions:</h3>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li><strong className="text-purple-700">Admin:</strong> Full access - manage all content, users, and approve articles</li>
          <li><strong className="text-blue-700">Author:</strong> Can write articles (pending approval), manage comments on own articles</li>
          <li><strong className="text-gray-700">User:</strong> Can read content and post comments</li>
        </ul>
        <div className="mt-3 pt-3 border-t border-border">
          <h4 className="font-medium text-foreground mb-1">User Actions:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li><strong className="text-orange-700">Suspend:</strong> Blocks user from reading articles and commenting</li>
            <li><strong className="text-red-700">Delete:</strong> Removes user and all their comments. Articles are preserved.</li>
          </ul>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-background border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 font-medium text-foreground">User</th>
                <th className="text-left p-4 font-medium text-foreground">Email</th>
                <th className="text-left p-4 font-medium text-foreground">Status</th>
                <th className="text-left p-4 font-medium text-foreground">Role</th>
                <th className="text-left p-4 font-medium text-foreground">Articles</th>
                <th className="text-left p-4 font-medium text-foreground">Comments</th>
                <th className="text-left p-4 font-medium text-foreground">Joined</th>
                <th className="text-right p-4 font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                [1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td colSpan={8} className="p-4">
                      <div className="h-6 bg-muted animate-pulse rounded"></div>
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-muted-foreground">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const role = roleConfig[user.role] || roleConfig.user;
                  const status = statusConfig[user.status] || statusConfig.active;
                  const RoleIcon = role.icon;
                  const isAdmin = user.role === 'admin';
                  const isSuspended = user.status === 'suspended';
                  
                  return (
                    <tr key={user.id} className={`hover:bg-muted/30 ${isSuspended ? 'opacity-60' : ''}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img 
                              src={user.picture} 
                              alt={user.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                              <span className="text-accent font-semibold">
                                {user.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <span className="font-medium text-foreground">{user.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">{user.email}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${role.color}`}>
                          <RoleIcon className="h-3 w-3" />
                          {role.label}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground text-center">
                        {user.articles_count || 0}
                      </td>
                      <td className="p-4 text-muted-foreground text-center">
                        {user.comments_count || 0}
                      </td>
                      <td className="p-4 text-muted-foreground text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {/* Role Selector */}
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleRoleChange(user.id, value, user.name)}
                            disabled={updating === user.id}
                          >
                            <SelectTrigger className="w-28" data-testid={`role-select-${user.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="author">Author</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {/* Suspend/Unsuspend Button - Not for admins */}
                          {!isAdmin && (
                            <Button
                              variant={isSuspended ? "outline" : "ghost"}
                              size="icon"
                              onClick={() => setSuspendDialog({ 
                                open: true, 
                                user, 
                                action: isSuspended ? 'unsuspend' : 'suspend' 
                              })}
                              disabled={updating === user.id}
                              title={isSuspended ? 'Unsuspend user' : 'Suspend user'}
                              data-testid={`suspend-btn-${user.id}`}
                            >
                              {isSuspended ? (
                                <Check className="h-4 w-4 text-green-600" />
                              ) : (
                                <Ban className="h-4 w-4 text-orange-600" />
                              )}
                            </Button>
                          )}
                          
                          {/* Delete Button - Not for admins */}
                          {!isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteDialog({ open: true, user })}
                              disabled={updating === user.id}
                              title="Delete user"
                              data-testid={`delete-btn-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suspend/Unsuspend Confirmation Dialog */}
      <AlertDialog open={suspendDialog.open} onOpenChange={(open) => !open && setSuspendDialog({ open: false, user: null, action: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              {suspendDialog.action === 'suspend' ? 'Suspend User' : 'Unsuspend User'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {suspendDialog.action === 'suspend' ? (
                <>
                  Are you sure you want to suspend <strong>{suspendDialog.user?.name}</strong>?
                  <br /><br />
                  They will be blocked from:
                  <ul className="list-disc list-inside mt-2">
                    <li>Reading restricted articles</li>
                    <li>Posting comments</li>
                    <li>Accessing author features (if applicable)</li>
                  </ul>
                </>
              ) : (
                <>
                  Are you sure you want to unsuspend <strong>{suspendDialog.user?.name}</strong>?
                  <br /><br />
                  They will regain access to all features based on their role.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSuspend}
              className={suspendDialog.action === 'suspend' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
            >
              {suspendDialog.action === 'suspend' ? 'Suspend' : 'Unsuspend'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, user: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete User Permanently
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.user?.name}</strong>?
              <br /><br />
              This action will:
              <ul className="list-disc list-inside mt-2">
                <li>Delete the user account permanently</li>
                <li>Delete all {deleteDialog.user?.comments_count || 0} comments by this user</li>
                <li><strong>Preserve</strong> {deleteDialog.user?.articles_count || 0} articles (author name remains for attribution)</li>
              </ul>
              <br />
              <strong className="text-red-600">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
